import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";

import { useAuth } from "../../contexts/AuthContext";
import { useTheme } from "../../contexts/ThemeContext";
import { useIsMobile } from "../../hooks/useIsMobile";
import LayoutDesktop from "../../layouts/LayoutDesktop";
import LayoutMobile from "../../layouts/LayoutMobile";

import { db } from "../../config/firebase";
import {
  doc,
  getDoc,
  serverTimestamp,
  updateDoc,
} from "firebase/firestore";
import { uploadToStorage } from "../../utils/storageUpload";

function normalizarTipoResposta(tipo) {
  const t = String(tipo || "").toUpperCase();
  if (t === "SIM/NAO" || t === "SIM_NAO") return "SIM_NAO";
  if (t === "OK/NOK" || t === "OK_NOK") return "OK_NOK";
  if (t === "NUM" || t === "NUMERO") return "NUMERO";
  if (t === "TEXT" || t === "TEXTO") return "TEXTO";
  if (t === "BAR") return "BAR";
  return t || "SIM_NAO";
}

function tituloTipoResposta(tipo) {
  const t = normalizarTipoResposta(tipo);
  if (t === "SIM_NAO") return "Sim / Não";
  if (t === "OK_NOK") return "OK / NOK";
  if (t === "NUMERO") return "Número";
  if (t === "TEXTO") return "Texto";
  if (t === "BAR") return "Barra";
  return t;
}

function criarRespostaBase(item) {
  return {
    pergunta: item?.pergunta || "",
    tipoResposta: normalizarTipoResposta(item?.tipoResposta),
    valor: "",
    obs: "",
    fotoUrl: item?.fotoUrl || null,
  };
}

export default function ChecklistExecutarPage() {
  const { id: maquinaId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useIsMobile();
  const { usuario: usuarioLogado } = useAuth();
  const { tema } = useTheme();

  // Determinar para onde voltar
  const voltarPara = location?.state?.voltarPara || null;
  const navegarVoltar = () => navigate(voltarPara ? voltarPara : -1);

  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState("");
  const [maquina, setMaquina] = useState(null);
  const [checklist, setChecklist] = useState(null);
  const [marca, setMarca] = useState("");
  const [modelo, setModelo] = useState("");
  const [numeroSerie, setNumeroSerie] = useState("");
  const [cor, setCor] = useState("");
  const [observacoes, setObservacoes] = useState("");
  const [respostas, setRespostas] = useState([]);
  const [salvando, setSalvando] = useState(false);
  const [sidebarAberta, setSidebarAberta] = useState(false);
  const [msgSucesso, setMsgSucesso] = useState("");
  const [fotos, setFotos] = useState([]);
  const [uploadandoFotos, setUploadandoFotos] = useState(false);

  const itensChecklist = useMemo(() => {
    const itens = checklist?.itens || [];
    return Array.isArray(itens) ? itens : [];
  }, [checklist]);

  useEffect(() => {
    let cancelado = false;

    async function carregar() {
      try {
        setLoading(true);
        setErro("");

        if (!usuarioLogado?.uid) {
          setErro("Você precisa estar logado.");
          return;
        }
        if (!maquinaId) {
          setErro("Equipamento inválido.");
          return;
        }

        // Carregar equipamento
        const maquinaRef = doc(db, "maquinas", maquinaId);
        const maquinaSnap = await getDoc(maquinaRef);
        
        if (!maquinaSnap.exists()) {
          setErro("Equipamento não encontrado.");
          return;
        }

        const maquinaData = { id: maquinaSnap.id, ...maquinaSnap.data() };
        console.log("Equipamento carregado:", maquinaData);
        
        if (cancelado) return;
        setMaquina(maquinaData);
        setMarca(String(maquinaData?.marca || ""));
        setModelo(String(maquinaData?.modelo || ""));
        setNumeroSerie(String(maquinaData?.numeroSerie || ""));
        setCor(String(maquinaData?.cor || ""));

        // Carregar modelo de checklist
        if (!maquinaData.checklistInstalacaoId) {
          setErro("Este equipamento não possui checklist de instalação associado.");
          return;
        }

        console.log("Carregando modelo de checklist:", maquinaData.checklistInstalacaoId);
        const checklistRef = doc(db, "checklistsModelos", maquinaData.checklistInstalacaoId);
        const checklistSnap = await getDoc(checklistRef);
        
        if (!checklistSnap.exists()) {
          setErro("Checklist não encontrado.");
          return;
        }

        const checklistData = { id: checklistSnap.id, ...checklistSnap.data() };
        console.log("Checklist carregado:", checklistData);
        
        if (cancelado) return;
        setChecklist(checklistData);

        const itensParaUsar = checklistData.itens || [];
        const base = itensParaUsar.map((item) => criarRespostaBase(item));

        setRespostas(base);
      } catch (e) {
        console.error(e);
        setErro("Falha ao carregar checklist.");
      } finally {
        if (!cancelado) setLoading(false);
      }
    }

    carregar();
    return () => {
      cancelado = true;
    };
  }, [maquinaId, usuarioLogado?.uid]);

  function setResposta(index, patch) {
    setRespostas((prev) => {
      const novo = [...prev];
      novo[index] = { ...(novo[index] || {}), ...patch };
      return novo;
    });
  }

  function validar() {
    for (let i = 0; i < respostas.length; i++) {
      const r = respostas[i];
      const t = normalizarTipoResposta(r?.tipoResposta);
      const v = String(r?.valor ?? "").trim();
      const pergunta = (r?.pergunta || "(sem pergunta)").trim();

      if (t === "SIM_NAO" && !["SIM", "NAO"].includes(v.toUpperCase())) {
        return `Responda: "${pergunta}" (Sim/Não).`;
      }
      if (t === "OK_NOK" && !["OK", "NOK"].includes(v.toUpperCase())) {
        return `Responda: "${pergunta}" (OK/NOK).`;
      }
      if ((t === "TEXTO" || t === "NUMERO" || t === "BAR") && !v) {
        return `Responda: "${pergunta}".`;
      }
    }
    return "";
  }

  async function handleAdicionarFotos(event) {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    const tiposFotoPermitidos = ["image/jpeg", "image/jpg", "image/png", "image/webp"];

    setUploadandoFotos(true);
    setErro("");
    setMsgSucesso("");

    const novasFotos = [];

    try {
      for (let i = 0; i < files.length; i++) {
        const file = files[i];

        if (!file.type.startsWith("image/")) continue;

        if (!tiposFotoPermitidos.includes(String(file.type || "").toLowerCase())) {
          setErro(`Formato não suportado para ${file.name}. Use JPG, PNG ou WEBP.`);
          continue;
        }

        const maxSize = 10 * 1024 * 1024;
        if (file.size > maxSize) {
          setErro(`Arquivo ${file.name} é muito grande (máximo 10MB).`);
          continue;
        }

        const url = await uploadToStorage({
          grupoId: usuarioLogado?.grupoId,
          empresaId: maquina?.empresaId || null,
          pasta: "checklists_fotos",
          file,
          filenamePrefix: `checklist_${maquinaId}_${Date.now()}`,
        });

        novasFotos.push({
          url,
          nome: file.name,
          uploadEm: new Date().toISOString(),
        });
      }

      if (novasFotos.length > 0) {
        setFotos((prev) => [...prev, ...novasFotos]);
        setMsgSucesso(`${novasFotos.length} foto(s) adicionada(s) com sucesso!`);
      }
    } catch (e) {
      console.error("Erro ao adicionar fotos do checklist:", e);
      setErro(`Erro ao fazer upload das fotos: ${e?.message || e}`);
    } finally {
      setUploadandoFotos(false);
      event.target.value = "";
    }
  }

  function removerFoto(idx) {
    setFotos((prev) => prev.filter((_, i) => i !== idx));
  }

  async function salvar() {
    try {
      setErro("");
      const msg = validar();
      if (msg) {
        setErro(msg);
        return;
      }

      setSalvando(true);

      const payload = {
        tipoChecklist: "INSTALACAO",
        checklistNome: checklist?.nome || "Checklist",
        checklistModeloId: maquina?.checklistInstalacaoId,
        checklistModeloNome: checklist?.nome,
        equipamento: {
          marca: String(marca || "").trim(),
          modelo: String(modelo || "").trim(),
          numeroSerie: String(numeroSerie || "").trim(),
          cor: String(cor || "").trim(),
        },
        respostas: respostas.map((r) => ({
          pergunta: (r?.pergunta || "").trim(),
          tipoResposta: normalizarTipoResposta(r?.tipoResposta),
          valor: r?.valor ?? "",
          obs: r?.obs ?? "",
          fotoUrl: r?.fotoUrl || null,
        })),
        observacoes: observacoes || "",
        fotos: fotos.map((f) => ({ url: f.url, nome: f.nome, uploadEm: f.uploadEm })) || [],
      };

      // Atualizar o equipamento com as respostas do checklist
      const maquinaRef = doc(db, "maquinas", maquinaId);
      const maquinaUpdate = {
        marca: String(marca || "").trim(),
        modelo: String(modelo || "").trim(),
        numeroSerie: String(numeroSerie || "").trim(),
        cor: String(cor || "").trim(),
        instaladoEm: serverTimestamp(),
        montadoPor: usuarioLogado?.nome || usuarioLogado?.email || "",
        montadoPorId: usuarioLogado?.uid || "",
        checklistUltimaExecucao: {
          ...payload,
          executadoEm: serverTimestamp(),
          executadoPorId: usuarioLogado.uid,
          executadoPorNome: usuarioLogado.nome || usuarioLogado.email || "",
        },
        checklistConcluido: true,
        checklistConcluidoEm: serverTimestamp(),
        atualizadoEm: serverTimestamp(),
      };

      await updateDoc(maquinaRef, maquinaUpdate);

      setMsgSucesso("Checklist salvo com sucesso!");
      setTimeout(() => navegarVoltar(), 1500);
    } catch (e) {
      console.error(e);
      setErro("Falha ao salvar checklist.");
    } finally {
      setSalvando(false);
    }
  }

  useEffect(() => {
    if (!msgSucesso) return;
    const t = setTimeout(() => setMsgSucesso(""), 3500);
    return () => clearTimeout(t);
  }, [msgSucesso]);

  const header = (
    <header
      style={{
        display: "flex",
        justifyContent: "space-between",
        gap: isMobile ? 10 : 12,
        alignItems: isMobile ? "stretch" : "center",
        flexDirection: isMobile ? "column" : "row",
      }}
    >
      <div>
        <h3 style={{ margin: 0, lineHeight: 1.15 }}>Checklist de Instalação</h3>
        <p style={{ marginTop: 6, opacity: 0.85, lineHeight: 1.4 }}>
          Equipamento: {maquina?.codigo || maquina?.id}
          {checklist?.nome ? ` · ${checklist.nome}` : ""}
          {maquina?.setorNome ? ` · ${maquina.setorNome}` : ""}
        </p>
      </div>
      <div style={{ display: "flex", gap: 8, flexDirection: isMobile ? "column" : "row", width: isMobile ? "100%" : "auto" }}>
        <button
          type="button"
          className="pc-btn-secundario"
          onClick={navegarVoltar}
          style={{ width: isMobile ? "100%" : "auto" }}
        >
          Voltar
        </button>
        <button
          type="button"
          className="pc-btn-secundario"
          onClick={() =>
            navigate("/ordens-servico/abrir-nova-os", {
              state: { equipamentoId: maquina?.id, equipamento: maquina },
            })
          }
          disabled={loading || !maquina?.id}
          style={{ width: isMobile ? "100%" : "auto" }}
        >
          Fazer atendimento
        </button>
        <button
          type="button"
          className="pc-btn-primario"
          onClick={salvar}
          disabled={salvando || loading}
          style={{ width: isMobile ? "100%" : "auto" }}
        >
          {salvando ? "Salvando..." : "Salvar"}
        </button>
      </div>
    </header>
  );

  const body = (
    <section
      className="pc-card"
      style={{
        maxWidth: isMobile ? "100%" : 980,
        width: "100%",
        margin: "0 auto",
      }}
    >
      <div>
        {header}
      </div>

      {msgSucesso && (
        <div
          style={{
            marginTop: 12,
            padding: 10,
            borderRadius: 10,
            background: "#14532d",
            color: "#bbf7d0",
            fontSize: 14,
          }}
        >
          {msgSucesso}
        </div>
      )}

      {erro && (
        <div
          style={{
            marginTop: 12,
            padding: 10,
            borderRadius: 10,
            background: "#7f1d1d",
            color: "#fecaca",
            fontSize: 14,
          }}
        >
          {erro}
        </div>
      )}

      <div style={{ marginTop: 14 }}>
        <div
          style={{
            padding: isMobile ? 12 : 18,
            borderRadius: 12,
            border: "1px solid rgba(148,163,184,0.35)",
            background: "rgba(255,255,255,0.04)",
            marginBottom: isMobile ? 12 : 18,
          }}
        >
          <div style={{ fontWeight: 700, marginBottom: isMobile ? 8 : 12 }}>Dados do equipamento</div>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: isMobile ? "1fr" : "repeat(4, minmax(0, 1fr))",
              gap: isMobile ? 10 : 14,
            }}
          >
            <div>
              <label style={{ display: "block", marginBottom: 6, fontSize: 13, opacity: 0.9 }}>Marca</label>
              <input
                className="pc-input"
                value={marca}
                onChange={(e) => setMarca(e.target.value)}
                placeholder="Marca"
                style={{ width: "100%" }}
              />
            </div>
            <div>
              <label style={{ display: "block", marginBottom: 6, fontSize: 13, opacity: 0.9 }}>Modelo</label>
              <input
                className="pc-input"
                value={modelo}
                onChange={(e) => setModelo(e.target.value)}
                placeholder="Modelo"
                style={{ width: "100%" }}
              />
            </div>
            <div>
              <label style={{ display: "block", marginBottom: 6, fontSize: 13, opacity: 0.9 }}>Número de Série</label>
              <input
                className="pc-input"
                value={numeroSerie}
                onChange={(e) => setNumeroSerie(e.target.value)}
                placeholder="Número de série"
                style={{ width: "100%" }}
              />
            </div>
            <div>
              <label style={{ display: "block", marginBottom: 6, fontSize: 13, opacity: 0.9 }}>Cor</label>
              <input
                className="pc-input"
                value={cor}
                onChange={(e) => setCor(e.target.value)}
                placeholder="Cor"
                style={{ width: "100%" }}
              />
            </div>
          </div>
        </div>

        {loading ? (
          <p style={{ padding: 10 }}>Carregando...</p>
        ) : !maquina ? (
          <p style={{ padding: 10 }}>Equipamento não encontrado.</p>
        ) : !checklist ? (
          <p style={{ padding: 10 }}>Checklist não encontrado.</p>
        ) : itensChecklist.length === 0 ? (
          <p style={{ padding: 10 }}>Este checklist não possui perguntas.</p>
        ) : (
          <div style={{ display: "grid", gap: 12 }}>

          {itensChecklist.map((item, idx) => {
            const r = respostas[idx] || criarRespostaBase(item);
            const tipo = normalizarTipoResposta(r.tipoResposta || item.tipoResposta);
            const valor = String(r.valor ?? "");
            const pergunta = String(item?.pergunta || "").trim() || `Item ${idx + 1}`;

            return (
              <div
                key={`${idx}_${item?.pergunta || ""}`}
                style={{
                  padding: 12,
                  borderRadius: 12,
                  border: "1px solid rgba(148,163,184,0.35)",
                  background: "rgba(255,255,255,0.04)",
                }}
              >
                <div style={{ display: "flex", justifyContent: "space-between", gap: 10 }}>
                  <strong>{idx + 1}. {pergunta}</strong>
                </div>

                {tipo === "SIM_NAO" && (
                  <div style={{ marginTop: 10 }}>
                    <select
                      className="pc-input"
                      style={{ width: "100%" }}
                      value={valor}
                      onChange={(e) => setResposta(idx, { valor: e.target.value })}
                    >
                      <option value="">Selecione...</option>
                      <option value="SIM">Sim</option>
                      <option value="NAO">Não</option>
                    </select>
                  </div>
                )}

                {tipo === "OK_NOK" && (
                  <div style={{ marginTop: 10 }}>
                    <select
                      className="pc-input"
                      style={{ width: "100%" }}
                      value={valor}
                      onChange={(e) => setResposta(idx, { valor: e.target.value })}
                    >
                      <option value="">Selecione...</option>
                      <option value="OK">OK</option>
                      <option value="NOK">NOK</option>
                    </select>
                  </div>
                )}

                {(tipo === "NUMERO" || tipo === "BAR") && (
                  <div style={{ marginTop: 10 }}>
                    <input
                      className="pc-input"
                      style={{ width: "100%" }}
                      type="number"
                      value={valor}
                      onChange={(e) => setResposta(idx, { valor: e.target.value })}
                      placeholder="Digite o valor"
                    />
                  </div>
                )}

                {tipo === "TEXTO" && (
                  <div style={{ marginTop: 10 }}>
                    <textarea
                      className="pc-input"
                      style={{ width: "100%" }}
                      rows={3}
                      value={valor}
                      onChange={(e) => setResposta(idx, { valor: e.target.value })}
                      placeholder="Digite..."
                    />
                  </div>
                )}

                {item?.fotoUrl && (
                  <div style={{ marginTop: 10 }}>
                    <div style={{ fontSize: 12, opacity: 0.8, marginBottom: 6 }}>Foto de referência</div>
                    <img
                      src={item.fotoUrl}
                      alt="foto"
                      style={{ maxWidth: "100%", borderRadius: 8 }}
                    />
                  </div>
                )}
              </div>
            );
          })}
          </div>
        )}

        <div style={{ marginTop: 16 }}>
          <label style={{ display: "block", marginBottom: 6, fontWeight: 600 }}>
            Observações (opcional)
          </label>
          <textarea
            rows={3}
            className="pc-input"
            value={observacoes}
            onChange={(e) => setObservacoes(e.target.value)}
            placeholder="Ex.: observações gerais da instalação..."
          />
        </div>

        <div style={{ marginTop: 16 }}>
          <label style={{ display: "block", marginBottom: 6, fontWeight: 600 }}>
            Fotos (opcional)
          </label>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 12 }}>
            <div
              style={{
                padding: 12,
                borderRadius: 8,
                border: "2px dashed rgba(148,163,184,0.5)",
                background: "rgba(148,163,184,0.08)",
                textAlign: "center",
                cursor: uploadandoFotos ? "not-allowed" : "pointer",
                opacity: uploadandoFotos ? 0.6 : 1,
              }}
            >
              <input
                type="file"
                multiple
                accept="image/jpeg,image/jpg,image/png,image/webp"
                onChange={handleAdicionarFotos}
                disabled={uploadandoFotos}
                style={{ display: "none" }}
                id="checklist-input-fotos-galeria"
              />
              <label htmlFor="checklist-input-fotos-galeria" style={{ cursor: uploadandoFotos ? "not-allowed" : "pointer", display: "block" }}>
                {uploadandoFotos ? "Enviando..." : (
                  <span>
                    🖼️ Galeria
                    <br />
                    <small>Selecionar fotos</small>
                  </span>
                )}
              </label>
            </div>

            <div
              style={{
                padding: 12,
                borderRadius: 8,
                border: "2px dashed rgba(148,163,184,0.5)",
                background: "rgba(148,163,184,0.08)",
                textAlign: "center",
                cursor: uploadandoFotos ? "not-allowed" : "pointer",
                opacity: uploadandoFotos ? 0.6 : 1,
              }}
            >
              <input
                type="file"
                accept="image/jpeg,image/jpg,image/png,image/webp"
                capture="environment"
                onChange={handleAdicionarFotos}
                disabled={uploadandoFotos}
                style={{ display: "none" }}
                id="checklist-input-fotos-camera"
              />
              <label htmlFor="checklist-input-fotos-camera" style={{ cursor: uploadandoFotos ? "not-allowed" : "pointer", display: "block" }}>
                {uploadandoFotos ? "Enviando..." : (
                  <span>
                    📸 Câmera
                    <br />
                    <small>Tirar foto</small>
                  </span>
                )}
              </label>
            </div>
          </div>

          {fotos.length > 0 && (
            <div style={{ marginTop: 12 }}>
              <div style={{ fontSize: 13, fontWeight: 500, marginBottom: 8 }}>
                {fotos.length} foto(s) adicionada(s)
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(100px, 1fr))", gap: 8 }}>
                {fotos.map((foto, idx) => (
                  <div
                    key={idx}
                    style={{
                      position: "relative",
                      borderRadius: 8,
                      overflow: "hidden",
                      border: "1px solid rgba(148,163,184,0.35)",
                    }}
                  >
                    <img
                      src={foto.url}
                      alt={`Foto ${idx + 1}`}
                      style={{ width: "100%", height: 100, objectFit: "cover", display: "block" }}
                    />
                    <button
                      type="button"
                      onClick={() => removerFoto(idx)}
                      style={{
                        position: "absolute",
                        top: 4,
                        right: 4,
                        width: 24,
                        height: 24,
                        borderRadius: "50%",
                        background: "rgba(239, 68, 68, 0.9)",
                        border: "none",
                        color: "white",
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: 12,
                      }}
                      title="Remover foto"
                    >
                      ✕
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </section>
  );

  if (isMobile) {
    return (
      <LayoutMobile
        tema={tema}
        titulo="Checklist"
        sidebarAberta={sidebarAberta}
        onToggleSidebar={() => setSidebarAberta((v) => !v)}
        abaBottom="home"
      >
        {body}
      </LayoutMobile>
    );
  }

  return (
    <LayoutDesktop
      tema={tema}
      titulo="Checklist de Instalação"
      subtitulo="Preencha todas as perguntas"
    >
      {body}
    </LayoutDesktop>
  );
}
